// Бейджі: Badge (тон + текст), StatusPill (значення системного enum з @unisched/shared — підпис і тон звідти),
// DataStatusPill (службова позначка даних meta.status, SPEC D39).
import { enumLabel, enumTone, type EnumName, type StatusTone } from '@unisched/shared';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type BadgeTone = StatusTone | 'primary' | 'accent';

const tones: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-soft text-neutral-ink',
  info: 'bg-info-soft text-info-ink',
  success: 'bg-success-soft text-success-ink',
  warning: 'bg-warning-soft text-warning-ink',
  danger: 'bg-danger-soft text-danger-ink',
  primary: 'bg-primary-soft text-primary-ink',
  accent: 'bg-accent-soft text-accent-ink',
};

const dots: Record<BadgeTone, string> = {
  neutral: 'bg-neutral',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  primary: 'bg-primary',
  accent: 'bg-accent',
};

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  /** Крапка кольору тону перед текстом. */
  dot?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  title?: string;
}

export function Badge({ tone = 'neutral', children, dot, size = 'md', className, title }: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-chip font-semibold whitespace-nowrap',
        size === 'sm' ? 'h-5 px-1.5 text-[11px]' : 'h-6 px-2 text-[12px]',
        tones[tone],
        className,
      )}
    >
      {dot ? <span className={cn('size-1.5 shrink-0 rounded-full', dots[tone])} aria-hidden /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
}

export interface StatusPillProps {
  /** Ім'я enum-а з ENUM_REGISTRY (ScheduleStatus, RunStatus, ConflictSeverity …). */
  enum: EnumName;
  value: string | null | undefined;
  size?: 'sm' | 'md';
  className?: string;
}

/** Статус системного enum-а: підпис і колір — з @unisched/shared (enumLabel / enumTone). */
export function StatusPill({ enum: name, value, size, className }: StatusPillProps) {
  if (!value) return <span className="text-faint">—</span>;
  return (
    <Badge tone={enumTone(name, value)} dot size={size} className={className}>
      {enumLabel(name, value)}
    </Badge>
  );
}

/**
 * Тон службової позначки даних (meta.status, D39). Значення — вільний текст з файлів довідників
 * («Відкриті дані», «Уточнити», «Підтверджено» …), тож тон визначається за основою слова.
 */
export function dataStatusTone(status: string): StatusTone {
  const s = status.toLowerCase();
  if (/уточн|перевір|сумнів|немає|відсутн/.test(s)) return 'warning';
  if (/підтвердж|затвердж|перевірено|офіційн/.test(s)) return 'success';
  if (/відкрит|публічн|сайт/.test(s)) return 'info';
  if (/помилк|недійсн|застар/.test(s)) return 'danger';
  return 'neutral';
}

/** Позначка статусу даних з meta.status (D39) з джерелом у підказці. */
export function DataStatusPill({ status, source, size = 'sm' }: { status?: string | null; source?: string | null; size?: 'sm' | 'md' }) {
  if (!status) return null;
  return (
    <Badge tone={dataStatusTone(status)} size={size} title={source ? `Джерело: ${source}` : undefined}>
      {status}
    </Badge>
  );
}
